import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import MessageLogApi from '../messageLog/messageLogApi';
import CustomerApi from '../customer/customerApi';
import { MessageLog } from '../messageLog/messageLogType';
import { Customer } from '../customer/customerType';
import { formatTimestamp, formatAddress } from '../lib/global_function';
import { Alert } from '../components/ui/alert';
import { Select } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Link } from '../components/ui/link';
import { Badge } from '../components/ui/badge';
import Spinner from '../components/ui/spinner';

interface MessageLogWrapper {
  id: string;
  type: string;
  attributes: MessageLog;
}

const DocumentSentPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Customer', path: '/main/customer' },
    { label: 'Document Sent', path: '/main/customer/documents_sent' },
  ];

  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [estimateEmails, setEstimateEmails] = useState<MessageLogWrapper[]>([]);
  const [estimateSms, setEstimateSms] = useState<MessageLogWrapper[]>([]);
  const [invoiceEmails, setInvoiceEmails] = useState<MessageLogWrapper[]>([]);
  const [invoiceSms, setInvoiceSms] = useState<MessageLogWrapper[]>([]);
  const [paymentStatementEmails, setPaymentStatementEmails] = useState<MessageLogWrapper[]>([]);
  const [paymentStatementSms, setPaymentStatementSms] = useState<MessageLogWrapper[]>([]);
  const [activeTab, setActiveTab] = useState<string>('estimate');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');

  const location = useLocation();
  const customerFromState = location.state?.customer as Customer | undefined;

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        if (customerFromState) {
          setSelectedCustomerId(customerFromState.customer_id);
        } else {
          const fetchedCustomers = await CustomerApi.get();
          setCustomers(fetchedCustomers);
        }
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [customerFromState]);

  useEffect(() => {
    const fetchMessageLogs = async () => {
      if (selectedCustomerId) {
        setLoading(true);
        setEstimateEmails([]);
        setEstimateSms([]);
        setInvoiceEmails([]);
        setInvoiceSms([]);
        setPaymentStatementEmails([]);
        setPaymentStatementSms([]);
        try {
          const response = await MessageLogApi.getMessageLogByCustomer(selectedCustomerId);
          setEstimateEmails(response?.estimate?.email?.data || []);
          setEstimateSms(response?.estimate?.sms?.data || []);
          setInvoiceEmails(response?.invoice?.email?.data || []);
          setInvoiceSms(response?.invoice?.sms?.data || []);
          setPaymentStatementEmails(response?.payment_statement?.email?.data || []);
          setPaymentStatementSms(response?.payment_statement?.sms?.data || []);
        } catch (error) {
          if (error instanceof Error) {
            setErrorMessage(error.message);
          } else {
            setErrorMessage('An unknown error occurred.');
          }
        } finally {
          setLoading(false);
        }
      } else {
        setEstimateEmails([]);
        setEstimateSms([]);
        setInvoiceEmails([]);
        setInvoiceSms([]);
        setPaymentStatementEmails([]);
        setPaymentStatementSms([]);
      }
    };

    fetchMessageLogs();
  }, [selectedCustomerId]);

  const handleCustomerChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newCustomerId = event.target.value;
    setSelectedCustomerId(newCustomerId);
    setErrorMessage('');
  };

  const selectedCustomer = customers.find(c => c.customer_id === selectedCustomerId) || customerFromState;

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('success') || statusLower.includes('delivered')) {
      return <Badge variant="success">{status}</Badge>;
    } else if (statusLower.includes('pending') || statusLower.includes('processing')) {
      return <Badge variant="warning">{status}</Badge>;
    } else if (statusLower.includes('failed') || statusLower.includes('error')) {
      return <Badge variant="error">{status}</Badge>;
    }
    return <Badge variant="secondary">{status}</Badge>;
  };

  const renderTable = (data: MessageLogWrapper[], type: 'sms' | 'email') => {
    if (data.length === 0) {
      return <Alert variant="info">No {type.toUpperCase()} messages found.</Alert>;
    }

    return (
      <>
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Subject</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                  {type === 'sms' ? 'Recipient Phone' : 'Recipient Email'}
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Attachment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {data.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-paragraph whitespace-nowrap">
                    {formatTimestamp(item.attributes.created_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-paragraph">
                    {item.attributes.message_subject || 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm text-paragraph">
                    {type === 'sms' 
                      ? (item.attributes.recipient_phone || 'N/A')
                      : (item.attributes.recipient_email || 'N/A')
                    }
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(item.attributes.message_processing_status)}
                  </td>
                  <td className="px-4 py-3">
                    {item.attributes.has_attachment && item.attributes.s3_url ? (
                      <Link to={item.attributes.s3_url} external variant="primary" underline="always">
                        View
                      </Link>
                    ) : (
                      <span className="text-sm text-muted">No</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {data.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600">Date: </span>
                  <span className="text-paragraph">{formatTimestamp(item.attributes.created_at)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Subject: </span>
                  <span className="text-paragraph">{item.attributes.message_subject || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-600">
                    {type === 'sms' ? 'Recipient Phone: ' : 'Recipient Email: '}
                  </span>
                  <span className="text-paragraph">
                    {type === 'sms' 
                      ? (item.attributes.recipient_phone || 'N/A')
                      : (item.attributes.recipient_email || 'N/A')
                    }
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Status: </span>
                  {getStatusBadge(item.attributes.message_processing_status)}
                </div>
                <div>
                  <span className="text-gray-600">Attachment: </span>
                  {item.attributes.has_attachment && item.attributes.s3_url ? (
                    <Link to={item.attributes.s3_url} external variant="primary" underline="always">
                      View
                    </Link>
                  ) : (
                    <span className="text-muted">No</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-semibold text-foreground mt-6 mb-6">
          Documents Sent to Customer
        </h2>

        {/* Customer Selector */}
        {!customerFromState && customers.length > 0 && (
          <div className="mb-6 max-w-3xl">
            <Label htmlFor="customer-select" className="mb-2">
              Select Customer
            </Label>
            <Select
              id="customer-select"
              value={selectedCustomerId}
              onChange={handleCustomerChange}
            >
              <option value="">Select a Customer</option>
              {customers.map((cust) => (
                <option key={cust.customer_id} value={cust.customer_id}>
                  {cust.first_name} {cust.last_name}, {formatAddress(cust.address_1, cust.address_2, cust.city, cust.state, cust.zip)}
                </option>
              ))}
            </Select>
          </div>
        )}

        {/* Selected Customer Info */}
        {selectedCustomer && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-foreground">
              <span className="font-semibold">Customer:</span> {selectedCustomer.first_name} {selectedCustomer.last_name}, {formatAddress(selectedCustomer.address_1, selectedCustomer.address_2, selectedCustomer.city, selectedCustomer.state, selectedCustomer.zip)}
            </p>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <Alert variant="error">
            {errorMessage}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Spinner />
        ) : (
          <>
            {/* Show message when no customer is selected */}
            {!selectedCustomerId && (
              <div className="text-center py-12">
                <p className="text-muted text-base">Please select a customer to view document tracking.</p>
              </div>
            )}

            {/* Tabs and Content - Show only when customer is selected */}
            {selectedCustomerId && (
              <div className="space-y-6">
                {/* Tab Buttons */}
                <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
                  <button
                    onClick={() => setActiveTab('estimate')}
                    className={`px-4 sm:px-6 py-3 text-sm sm:text-base font-semibold transition-colors rounded-t-lg whitespace-nowrap ${
                      activeTab === 'estimate'
                        ? 'text-primary border-b-2 border-primary bg-orange-50'
                        : 'text-paragraph hover:text-foreground hover:bg-gray-50'
                    }`}
                  >
                    Estimates
                  </button>
                  <button
                    onClick={() => setActiveTab('invoice')}
                    className={`px-4 sm:px-6 py-3 text-sm sm:text-base font-semibold transition-colors rounded-t-lg whitespace-nowrap ${
                      activeTab === 'invoice'
                        ? 'text-primary border-b-2 border-primary bg-orange-50'
                        : 'text-paragraph hover:text-foreground hover:bg-gray-50'
                    }`}
                  >
                    Invoices
                  </button>
                  <button
                    onClick={() => setActiveTab('payment_statement')}
                    className={`px-4 sm:px-6 py-3 text-sm sm:text-base font-semibold transition-colors rounded-t-lg whitespace-nowrap ${
                      activeTab === 'payment_statement'
                        ? 'text-primary border-b-2 border-primary bg-orange-50'
                        : 'text-paragraph hover:text-foreground hover:bg-gray-50'
                    }`}
                  >
                    Payment Statements
                  </button>
                </div>

                {/* Tab Content */}
                <div className="space-y-8">
                  {activeTab === 'estimate' && (
                    <>
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">SMS Messages</h3>
                        {renderTable(estimateSms, 'sms')}
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">Email Messages</h3>
                        {renderTable(estimateEmails, 'email')}
                      </div>
                    </>
                  )}

                  {activeTab === 'invoice' && (
                    <>
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">SMS Messages</h3>
                        {renderTable(invoiceSms, 'sms')}
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">Email Messages</h3>
                        {renderTable(invoiceEmails, 'email')}
                      </div>
                    </>
                  )}

                  {activeTab === 'payment_statement' && (
                    <>
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">SMS Messages</h3>
                        {renderTable(paymentStatementSms, 'sms')}
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4">Email Messages</h3>
                        {renderTable(paymentStatementEmails, 'email')}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default DocumentSentPage;